import { NavLink, Outlet, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import {
  Building2,
  User,
  Kanban,
  Wrench,
  FileText,
  BookOpen,
  Users,
  Shield,
  UserCog,
  Columns3,
  Upload,
  Download,
  Mail,
  FileCode2,
  MessageSquare,
  Bell,
  Plug,
  CalendarSync,
  Webhook,
  Key,
  ChevronDown,
  Tag,
  ClipboardList,
  type LucideIcon,
} from "lucide-react"
import { useState } from "react"

// ── Settings navigation structure ──────────────────────────────

interface SettingsNavItem {
  icon: LucideIcon
  label: string
  href: string
  hidden?: boolean
}

interface SettingsNavGroup {
  label: string
  items: SettingsNavItem[]
}

const settingsNav: SettingsNavGroup[] = [
  {
    label: "Og\u00F3lne",
    items: [
      { icon: Building2, label: "Profil firmy", href: "/settings/company", hidden: true },
      { icon: User, label: "M\u00F3j profil", href: "/settings/profile" },
    ],
  },
  {
    label: "Sprzeda\u017C",
    items: [
      { icon: Kanban, label: "Pipeline sprzeda\u017Cy", href: "/settings/deal-phases" },
      { icon: Wrench, label: "Etapy serwisu", href: "/settings/ticket-phases" },
      { icon: FileText, label: "Szablony ofert", href: "/settings/quote-templates" },
      { icon: BookOpen, label: "S\u0142owniki", href: "/settings/dictionaries" },
    ],
  },
  {
    label: "Zadania",
    items: [
      { icon: Tag, label: "Kategorie zada\u0144", href: "/settings/task-categories" },
      { icon: ClipboardList, label: "Szablony zada\u0144", href: "/settings/task-templates" },
    ],
  },
  {
    label: "Zesp\u00F3\u0142",
    items: [
      { icon: Users, label: "U\u017Cytkownicy", href: "/settings/users", hidden: true },
      { icon: Shield, label: "Grupy", href: "/settings/groups" },
      { icon: UserCog, label: "Uprawnienia", href: "/settings/permissions" },
    ],
  },
  {
    label: "Dane",
    items: [
      { icon: Columns3, label: "Pola niestandardowe", href: "/settings/custom-fields", hidden: true },
      { icon: Upload, label: "Import danych", href: "/settings/import", hidden: true },
      { icon: Download, label: "Eksport danych", href: "/settings/export" },
    ],
  },
  {
    label: "Komunikacja",
    items: [
      { icon: Mail, label: "Konta email", href: "/settings/email-accounts" },
      { icon: FileCode2, label: "Szablony email", href: "/settings/email-templates" },
      { icon: MessageSquare, label: "SMS", href: "/settings/sms-settings" },
      { icon: Bell, label: "Powiadomienia", href: "/settings/notifications" },
    ],
  },
  {
    label: "Integracje",
    items: [
      { icon: Plug, label: "Zewn\u0119trzne", href: "/settings/integrations" },
      { icon: CalendarSync, label: "Kalendarz", href: "/settings/calendar-sync" },
      { icon: Webhook, label: "Webhooki", href: "/settings/webhooks" },
      { icon: Key, label: "API & OAuth", href: "/settings/oauth" },
    ],
  },
]

// ── Mobile dropdown ────────────────────────────────────────────

function MobileSettingsNav() {
  const location = useLocation()
  const [open, setOpen] = useState(false)

  const visibleNav = settingsNav
    .map((g) => ({ ...g, items: g.items.filter((i) => !i.hidden) }))
    .filter((g) => g.items.length > 0)

  // Find current section label
  let currentLabel = "Ustawienia"
  for (const group of visibleNav) {
    for (const item of group.items) {
      if (location.pathname === item.href || location.pathname.startsWith(item.href + "/")) {
        currentLabel = item.label
      }
    }
  }

  return (
    <div className="relative mb-4 lg:hidden">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-[13px] font-medium text-foreground"
      >
        <span>{currentLabel}</span>
        <ChevronDown
          size={16}
          className={cn(
            "text-muted-foreground transition-transform",
            open ? "rotate-180" : ""
          )}
        />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[60vh] overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
            {visibleNav.map((group) => (
              <div key={group.label}>
                <div className="px-3 py-1.5 text-[11px] font-medium text-muted-foreground">
                  {group.label}
                </div>
                {group.items.map((item) => {
                  const isActive =
                    location.pathname === item.href ||
                    location.pathname.startsWith(item.href + "/")
                  const Icon = item.icon
                  return (
                    <NavLink
                      key={item.href}
                      to={item.href}
                      onClick={() => setOpen(false)}
                      className={cn(
                        "flex items-center gap-2.5 px-3 py-2 text-sm transition-colors",
                        isActive
                          ? "bg-primary/8 font-medium text-primary"
                          : "text-foreground hover:bg-muted"
                      )}
                    >
                      <Icon size={15} strokeWidth={isActive ? 2 : 1.75} />
                      <span>{item.label}</span>
                    </NavLink>
                  )
                })}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ── Sidebar nav item ───────────────────────────────────────────

function SettingsSidebarItem({ item }: { item: SettingsNavItem }) {
  const location = useLocation()
  const isActive =
    location.pathname === item.href ||
    location.pathname.startsWith(item.href + "/")
  const Icon = item.icon

  return (
    <NavLink
      to={item.href}
      className={cn(
        "flex h-[32px] items-center gap-2.5 rounded-md px-2.5 text-[13px] transition-colors",
        isActive
          ? "bg-primary/10 font-medium text-primary"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
    >
      <Icon size={15} strokeWidth={isActive ? 2 : 1.75} />
      <span className="truncate">{item.label}</span>
    </NavLink>
  )
}

// ── Main layout ────────────────────────────────────────────────

export function SettingsLayout() {
  const { t } = useTranslation()
  const location = useLocation()

  const visibleNav = settingsNav
    .map((g) => ({ ...g, items: g.items.filter((i) => !i.hidden) }))
    .filter((g) => g.items.length > 0)

  // Build breadcrumb: find current section
  let currentSectionLabel = ""
  for (const group of visibleNav) {
    for (const item of group.items) {
      if (location.pathname === item.href || location.pathname.startsWith(item.href + "/")) {
        currentSectionLabel = item.label
      }
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1200px]">
      {/* Breadcrumb */}
      <div className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground">
        <NavLink to="/dashboard" className="hover:text-foreground transition-colors">
          {t("nav.dashboard")}
        </NavLink>
        <span>/</span>
        <NavLink to="/settings" className="hover:text-foreground transition-colors">
          {t("settings.title")}
        </NavLink>
        {currentSectionLabel && (
          <>
            <span>/</span>
            <span className="text-foreground font-medium">{currentSectionLabel}</span>
          </>
        )}
      </div>

      {/* Mobile nav */}
      <MobileSettingsNav />

      {/* Desktop layout */}
      <div className="flex gap-6">
        {/* Sidebar */}
        <aside className="hidden w-[220px] shrink-0 lg:block">
          <nav className="sticky top-6 space-y-5">
            {visibleNav.map((group) => (
              <div key={group.label} className="space-y-0.5">
                <span className="block px-2.5 pb-1 text-[11px] font-medium text-muted-foreground">
                  {group.label}
                </span>
                {group.items.map((item) => (
                  <SettingsSidebarItem key={item.href} item={item} />
                ))}
              </div>
            ))}
          </nav>
        </aside>

        {/* Content area */}
        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  )
}
